import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { track } from "../analytics";
import { useShareCount, bumpShareCount } from "../shareCounts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHARE_BADGE_MIN = 3; // hide badge below this count (avoids "1 shared" sad-look)

function shareKeyFor(eventLabel, product) {
    if (eventLabel) return eventLabel;
    if (product) return `product:${product.id}`;
    return "generic";
}

/**
 * Generic "share this box" button.
 *
 * Two ways to use it:
 *   1. <ShareButton product={p} />        // legacy — derives title/text/url
 *   2. <ShareButton title="..." text="..." path="#partners" image="/img.jpg" />
 *
 * Behavior:
 *   • Mobile: opens native iOS/Android share sheet (Instagram, IG Stories, iMessage, Twitter, etc.)
 *   • Desktop: copies a referral-tagged link to clipboard + toast
 *
 * Open Graph: shared URLs point at /api/share so previews show the BOX'S OWN
 * image (not the default site image). The /api/share endpoint serves a tiny
 * HTML stub with dynamic og:image + meta-refresh + JS redirect to the real
 * homepage section. Bots see the meta tags; humans get instant redirected.
 *
 * Referral attribution is preserved: `?ref=xxx` is forwarded through.
 */
export default function ShareButton({
    product,
    title: titleProp,
    text: textProp,
    path,
    image: imageProp,
    eventLabel,
    className,
    "data-testid": dataTestId,
}) {
    const [copied, setCopied] = useState(false);
    const shareKey = shareKeyFor(eventLabel, product);
    const count = useShareCount(shareKey);
    const showBadge = count >= SHARE_BADGE_MIN;

    const buildShareUrl = () => {
        const origin = window.location.origin;
        const apiBase = process.env.REACT_APP_BACKEND_URL || origin;

        // NEW: products use the branded /api/share/p/{id} endpoint which
        // serves a real 1200x630 OG image rendered with product photo + price.
        if (product?.id) {
            const url = new URL(`${apiBase}/api/share/p/${product.id}`);
            try {
                const ref = localStorage.getItem("fgm_ref");
                if (ref) url.searchParams.set("ref", ref);
            } catch {
                /* localStorage blocked */
            }
            return url.toString();
        }

        // LEGACY fallback for non-product shares (partners, brand, etc.)
        const url = new URL(`${apiBase}/api/share`);
        const title = titleProp || "FGM Apparel — Focus. Grind. Motivate.";
        const desc = textProp || "Streetwear built for the relentless.";
        const img = imageProp || "/brand/ambassador.jpeg";
        url.searchParams.set("title", title);
        url.searchParams.set("desc", desc);
        url.searchParams.set("img", img);
        const targetPath = path || "";
        if (targetPath) url.searchParams.set("path", targetPath);
        try {
            const ref = localStorage.getItem("fgm_ref");
            if (ref) url.searchParams.set("ref", ref);
            const drop = localStorage.getItem("fgm_drop_code");
            if (drop) url.searchParams.set("drop", drop);
        } catch {
            /* localStorage blocked — skip silently */
        }
        return url.toString();
    };

    const handleShare = async () => {
        const url = buildShareUrl();
        const title =
            titleProp ||
            (product ? `${product.name} — FGM Apparel` : "FGM Apparel — Focus. Grind. Motivate.");
        const text =
            textProp ||
            (product
                ? `${product.name} — $${
                      Number.isInteger(product.price)
                          ? product.price
                          : product.price.toFixed(2)
                  }. Focus. Grind. Motivate.`
                : "Focus. Grind. Motivate. — Streetwear for athletes, creators, and the relentless.");

        track("share", {
            source: eventLabel || (product ? `product:${product.id}` : "generic"),
            url,
        });

        // Persist to backend (fire-and-forget) + optimistic local bump
        axios
            .post(`${API}/share/track`, { key: shareKey })
            .then((res) => bumpShareCount(shareKey, res.data?.count))
            .catch(() => bumpShareCount(shareKey));

        if (navigator.share) {
            try {
                await navigator.share({ title, text, url });
                return;
            } catch (err) {
                if (err?.name === "AbortError") return;
            }
        }

        try {
            await navigator.clipboard.writeText(`${text}\n${url}`);
            setCopied(true);
            toast.success("Link copied — drop it in your bio");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Couldn't copy. Long-press the URL bar to share.");
        }
    };

    const computedTestId =
        dataTestId ||
        (product ? `product-share-${product.id}` : `share-${(eventLabel || "btn").toLowerCase()}`);

    return (
        <>
            <button
                data-testid={computedTestId}
                onClick={handleShare}
                aria-label="Share"
                title={showBadge ? `Shared ${count} times — click to share` : "Share"}
                className={
                    className ||
                    "absolute top-3 right-3 z-10 h-8 w-8 flex items-center justify-center bg-black/80 backdrop-blur-md border border-white/20 hover:bg-[#E63946] hover:border-[#E63946] transition-colors"
                }
            >
                {copied ? (
                    <span className="text-white text-xs">✓</span>
                ) : (
                    <svg
                        viewBox="0 0 24 24"
                        className="w-4 h-4 stroke-white"
                        fill="none"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <circle cx="18" cy="5" r="3" />
                        <circle cx="6" cy="12" r="3" />
                        <circle cx="18" cy="19" r="3" />
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                )}
                {showBadge && (
                    <span
                        data-testid={`share-count-${shareKey}`}
                        className="absolute -bottom-2 -right-2 min-w-[22px] h-[18px] px-1 flex items-center justify-center bg-[#E63946] text-white text-[10px] font-display tracking-wider tabular-nums pointer-events-none"
                        aria-label={`Shared ${count} times`}
                    >
                        {count > 999 ? "999+" : count}
                    </span>
                )}
            </button>
        </>
    );
}
